import { useEffect, useRef, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function useOutside(ref, cb) {
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && cb();
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, cb]);
}

export function LocationInput({ value, onChange, testid = "location-input", placeholder = "Postcode or town, e.g. SL7 or Marlow" }) {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useOutside(ref, () => setOpen(false));
  useEffect(() => {
    if (!open || !value || value.length < 2) return setSugs([]);
    const t = setTimeout(() => api.get("/geo/suggest", { params: { q: value } }).then((r) => setSugs(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [value, open]);
  return (
    <div className="relative" ref={ref}>
      <MapPin size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input className="input pl-10" value={value} placeholder={placeholder} aria-label="Location" data-testid={testid}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      {open && sugs.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full card py-1 max-h-64 overflow-auto" role="listbox">
          {sugs.map((s) => (
            <li key={s.label}><button type="button" className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50" data-testid="location-suggestion"
              onClick={() => { onChange(s.label); setOpen(false); }}>{s.label}</button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ServiceInput({ value, onChange, onPick, testid = "service-input" }) {
  const { cats } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useOutside(ref, () => setOpen(false));
  const opts = cats.flatMap((c) => [{ label: c.name, cat: c.slug }, ...c.subcategories.map((s) => ({ label: s.name, cat: c.slug, sub: c.name }))]);
  const q = (value || "").toLowerCase();
  const list = (q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts.filter((o) => !o.sub)).slice(0, 8);
  return (
    <div className="relative" ref={ref}>
      <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input className="input pl-10" value={value} placeholder="e.g. Plumber, tap repair, fuse box" aria-label="Service" data-testid={testid}
        onChange={(e) => { onChange(e.target.value); setOpen(true); onPick?.(null); }} onFocus={() => setOpen(true)} />
      {open && list.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full card py-1 max-h-72 overflow-auto" role="listbox">
          {list.map((o) => (
            <li key={o.label + o.cat}><button type="button" className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex justify-between" data-testid="service-suggestion"
              onClick={() => { onChange(o.label); onPick?.(o.cat); setOpen(false); }}>
              <span>{o.label}</span>{o.sub && <span className="text-xs text-slate-400">{o.sub}</span>}
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
}
